// Single source of truth for the `textSizeClass` prop on core-rich-text and
// core-truncated-rich-text. Those components build the class at runtime
// (`!${textSizeClass}`), so UnoCSS's extractor never sees it in the source —
// uno.config.ts safelists this list instead, and the prop validators check
// against it. Keep the two in sync by importing from here, never by hand.
export const richTextSizeClasses = ['text-xs', 'text-sm', 'text-d', 'text-dm', 'text-md', 'text-lg']

export const defaultRichTextSizeClass = 'text-d'

// Breakpoint variants a size may be prefixed with (`text-md <sm:text-d`).
// presetWind's default breakpoints, in both min-width and max-width form.
const breakpoints = ['sm', 'md', 'lg', 'xl', '2xl']
export const richTextSizeVariants = breakpoints.flatMap(bp => [`${bp}:`, `<${bp}:`])

// Every class the components can emit — this is what uno.config.ts safelists.
export const richTextSizeSafelist = richTextSizeClasses.flatMap(size =>
  ['', ...richTextSizeVariants].map(variant => `${variant}!${size}`)
)

const tokenPattern = /^(<?[a-z0-9]+:)?!*(text-[a-z]+)$/

// Splits `md:!text-md` into its variant and size; null when either is unknown.
const parseToken = (token: string) => {
  const [, variant = '', size] = token.match(tokenPattern) ?? []

  if (!size || !richTextSizeClasses.includes(size)) return null
  if (variant && !richTextSizeVariants.includes(variant)) return null

  return { variant, size }
}

const splitTokens = (value: string) => value?.trim().split(/\s+/) ?? []

export const isValidRichTextSizeClass = (value: string) => {
  const tokens = splitTokens(value)

  return tokens.length > 0 && tokens.every(token => parseToken(token) !== null)
}

// Callers occasionally pass an already-prefixed `!text-sm`; prefixing that
// again yields `!!text-sm`, which matches no rule at all. Strip and re-add,
// keeping any breakpoint variant in front of the `!`.
export const normalizeRichTextSizeClass = (value: string) => {
  if (!isValidRichTextSizeClass(value)) {
    console.warn(`Invalid textSizeClass value: ${value}. Allowed sizes are: ${richTextSizeClasses.join(', ')}, optionally prefixed with ${richTextSizeVariants.join(', ')}`)
    return `!${defaultRichTextSizeClass}`
  }

  return splitTokens(value)
    .map(token => parseToken(token)!)
    .map(({ variant, size }) => `${variant}!${size}`)
    .join(' ')
}
